import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import { dateFormat } from 'app/appSettings'
import moment from 'moment'
import React, { useEffect } from 'react'

import { myI18n } from 'translation/I18nConnectedProvider'
import { parseActionTemplatesToScoring } from '../grants/AvaliableActionsUtils'
import FutureAspect from './FutureAspect'
import { getChartData } from './ImportanceChart'
import { getPdfTextFromSectionsArray, scoringData } from './SurveyScoreUtils'

const WEB_TO_PDF_SCALE = 0.40
Font.register({
  family: 'Open Sans',
  fonts: [
    { src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf' },
    { src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-600.ttf', fontWeight: 600 }
  ]
})

export const surveyPDFStyles = StyleSheet.create({
  page: {
    padding: 20
  },
  logo: {
    width: 100 * WEB_TO_PDF_SCALE,
    height: 100 * WEB_TO_PDF_SCALE,
    marginRight: 10
  },
  organizationTile: {
    fontSize: 35 * WEB_TO_PDF_SCALE,
    fontFamily: 'Open Sans',
    fontWeight: 700
  },
  organizationDetailsBold: {
    fontSize: 16 * WEB_TO_PDF_SCALE,
    fontFamily: 'Open Sans',
    fontWeight: 600
  },
  text: {
    fontSize: 16 * WEB_TO_PDF_SCALE,
    fontFamily: 'Open Sans',
    fontWeight: 400
  },
  paragraphTitle: {
    fontSize: 30 * WEB_TO_PDF_SCALE,
    fontWeight: 400,
    lineHeight: '25px'
  },
  keyThemeContainer: {
    paddingTop: 23 * WEB_TO_PDF_SCALE,
    paddingBottom: 36 * WEB_TO_PDF_SCALE,
    backgroundColor: '#EEEFE6'
  },
  keyThemetitle: {
    fontSize: 20 * WEB_TO_PDF_SCALE,
    fontWeight: 700
  },
  futureTitle: {
    fontSize: 24 * WEB_TO_PDF_SCALE,
    fontWeight: 400,
    minWidth: 120 * WEB_TO_PDF_SCALE
  },
  futureDescription: {
    fontSize: 14 * WEB_TO_PDF_SCALE,
    fontWeight: 400,
    flexWrap: 'wrap'
  },
  futureDivider: {
    width: '100%',
    height: 0.2,
    backgroundColor: 'rgb(219, 219, 217)',
    marginBottom: 15 * WEB_TO_PDF_SCALE,
    marginTop: 15 * WEB_TO_PDF_SCALE
  },
  scoreSummary: {
    paddingTop: 24 * WEB_TO_PDF_SCALE,
    paddingRight: 0,
    paddingBottom: 16 * WEB_TO_PDF_SCALE,
    paddingLeft: 40 * WEB_TO_PDF_SCALE,
    backgroundColor: '#17374F'
  },
  card: {
    borderWidth: 0.5,
    borderColor: '#17374F',
    padding: 8
  },
  cardTitle: {
    fontSize: 20 * WEB_TO_PDF_SCALE,
    fontWeight: 600
  },
  cardDescription: {
    fontSize: 14 * WEB_TO_PDF_SCALE,
    fontWeight: 400
  },
  actionsParagraph: {
    fontSize: 24 * WEB_TO_PDF_SCALE,
    fontWeight: 600
  },
  textBold: {
    fontSize: 14 * WEB_TO_PDF_SCALE,
    fontWeight: 'ultrabold'
  },
  grid: {
    flexDirection: 'row'
  },
  gridItem: {
    width: '33.33%', // 3 columns -> each column takes 33.33% of the width
    padding: 5 // Add some space between grid items
  },
  fixedImage: {
    height: 50
  },
  chartImage: {
    height: 140,
    width: 150
  }
})

const CreateActionCard = ({
  item
}) => {
  return (
    <View
      style={[{
        minHeight: '60px',
        flex: 1
      }, surveyPDFStyles.card]}
    >
      <Text style={[surveyPDFStyles.cardTitle]}>
        {item.name}
      </Text>
      <Text
        style={[
          {
            marginTop: 10,
            marginBottom: 10,
            width: '100%'
          },
          surveyPDFStyles.cardDescription
        ]}
      >
        {item.description}
      </Text>
      <Text
        style={[{
          marginTop: 10,
          marginBottom: 10,
          width: '100%'
        }, surveyPDFStyles.cardDescription]}
      >
        <Text style={surveyPDFStyles.textBold}>
          {myI18n._('Expected outcome:')} {' '}
        </Text>
        {item.outcome}
      </Text>
    </View>
  )
}

function AvaliableActions ({
  template = {},
  skippedSections = [],
  actionTemplates,
  surveyAnswers,
  scoreCard,
  sectionScores,
  user
}) {
  const [templatesList, setTemplatesList] = React.useState([])

  useEffect(() => {
    setTemplatesList(
      parseActionTemplatesToScoring({
        actionTemplates,
        user,
        skippedSections,
        sectionScores,
        surveyTemplateId: template.id,
        scoreCard,
        surveyAnswers
      })
    )
  }, [actionTemplates, user.language])

  const priorityList = templatesList.slice(0, 6)
  const nonPriorityList = templatesList.slice(6)

  const getTextHeight = (text, fontSize, lineHeight = 1.2) => {
    const avgCharWidth = fontSize * 0.7
    const charsPerLine = Math.floor(250 / avgCharWidth)
    const lines = Math.ceil(text.length / charsPerLine)
    const baseHeight = lines * fontSize * lineHeight

    const padding = fontSize * 1.5
    return baseHeight + padding
  }

  const chunkList = (list, size) => {
    const result = []
    const fontSize = 11
    const lineHeight = 1.2

    for (let i = 0; i < list.length; i += size) {
      const chunk = list.slice(i, i + size)

      let longestItemHeight = 0

      chunk.forEach(item => {
        const nameHeight = getTextHeight(item.name, fontSize, lineHeight) * 1.2
        const descriptionHeight = getTextHeight(item.description, fontSize, lineHeight) * 0.8
        const outcomeHeight = getTextHeight(item.outcome, fontSize, lineHeight) * 0.8
        const totalHeight = nameHeight + descriptionHeight + outcomeHeight

        if (totalHeight > longestItemHeight) {
          longestItemHeight = totalHeight
        }
      })

      result.push({ chunk, length: longestItemHeight })
    }

    return result
  }

  const priorityChunks = chunkList(priorityList, 3)
  const nonPriorityChunks = chunkList(nonPriorityList, 3)

  const noTemplates = priorityList.length === 0
  return (
    <View>
      {!noTemplates && (
        <View style={[surveyPDFStyles.container]}>
          <Text style={[surveyPDFStyles.paragraphTitle, { marginTop: 30 }]}>
            {myI18n._('SCORE_CARD_ACTIONS_SUGGESTED_ACTIONS_TITLE')}
          </Text>

          <Text style={[surveyPDFStyles.actionsParagraph, { marginTop: 7, marginBottom: 5 }]}>
            {myI18n._('SCORE_CARD_ACTIONS_TOP_SUGGESTIONS_TITLE')}
          </Text>

          {priorityChunks.map((chunkData, chunkIndex) => (
            <View
              style={{ flexDirection: 'row', marginLeft: -5, marginRight: -5 }}
              key={chunkIndex}
            >
              {chunkData.chunk.map((item, index) => (
                <View
                  wrap={false}
                  style={[surveyPDFStyles.gridItem, { height: chunkData.length * 0.6 }]}
                  key={index}
                >
                  <CreateActionCard item={item} />
                </View>
              ))}
            </View>
          ))}
          {Boolean(nonPriorityList.length > 0) && (
            <>
              <Text style={[surveyPDFStyles.actionsParagraph, { marginTop: 20, marginBottom: 5 }]}>
                {myI18n._('SCORE_CARD_ACTIONS_OTHER_SUGGESTED_ACTIONS_TITLE')}
              </Text>

              {nonPriorityChunks.map((chunkData, chunkIndex) => (
                <View
                  style={{ flexDirection: 'row', marginLeft: -5, marginRight: -5 }}
                  key={chunkIndex}
                >
                  {chunkData.chunk.map((item, index) => (
                    <View
                      wrap={false}
                      style={[surveyPDFStyles.gridItem, { height: chunkData.length }]}
                      key={index}
                    >
                      <CreateActionCard item={item} />
                    </View>
                  ))}
                </View>
              ))}
            </>
          )}
        </View>
      )}
      {noTemplates && (
        <View style={{ padding: 15 }}>
          <Text>
            {myI18n._('There are no actions for us to suggest')}
          </Text>
        </View>
      )}
    </View>
  )
}

const KeyTheme = ({
  name,
  skippedSections,
  sectionText,
  percent,
  item,
  averageText,
  averageName,
  averagePercent
}) => {
  return (
    <View style={[surveyPDFStyles.keyThemeContainer, { flexDirection: 'row' }]}>
      <View style={{ alignItems: 'center', width: '20%', marginTop: 5 }}>
        <Text style={[surveyPDFStyles.text, { marginBottom: 5 }]}>
          {averagePercent || (skippedSections.includes(item) ? myI18n._('NA') : myI18n._(percent))}
        </Text>
        <Text style={[surveyPDFStyles.text]}>
          {myI18n._('SCORE')}
        </Text>
      </View>
      <View style={{ width: '70%', minHeight: 40 }}>
        <View style={[{ justifyContent: 'center' }]}>
          <Text style={[surveyPDFStyles.keyThemetitle]}>
            {averageName || myI18n._(name)}
          </Text>
        </View>
        <View style={[{ paddingTop: 6 }]}>
          <Text style={surveyPDFStyles.text}>
            {averageText || (skippedSections.includes(item) ? myI18n._('NA') : myI18n._(sectionText))}
          </Text>
        </View>
      </View>
    </View>
  )
}

const SurveyScorePdf = ({
  survey,
  data,
  scoreData,
  esat,
  skippedSections,
  highestImportanceSections,
  importanceChartImg,
  futureText,
  sectionScores,
  scoreCard,
  actionTemplates,
  user
}) => {
  const chartData = getChartData(data, skippedSections)
  const chartPayload = chartData?.map(item => ({
    color: item.fill,
    value: item.label
  }))

  return (
    <Document>
      <Page>
        <View style={surveyPDFStyles.page}>
          <View fixed style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
            <Image
              style={surveyPDFStyles.fixedImage}
              src='/assets\images\centre_logo_full.png'
            />
          </View>
          <View
            style={[
              {
                flexDirection: 'row',
                marginBottom: 15
              }
            ]}
          >
            <Image
              style={surveyPDFStyles.logo}
              src={
                esat
                  ? '/assets/images/e-sat.png'
                  : '/assets/images/self-assesment_tool.png'
              }
            />
            <View>
              <Text style={[surveyPDFStyles.organizationTile, { marginBottom: 3 }]}>
                {myI18n._('Self Assessment')}
              </Text>
              <Text>
                {!survey.containsAggragatedData && (
                  <Text style={surveyPDFStyles.organizationDetailsBold}>
                    {myI18n._('Organization name')}: {' '}
                    <Text style={surveyPDFStyles.text}>{survey.accountName}; </Text>
                  </Text>
                )}
                <Text style={surveyPDFStyles.organizationDetailsBold}>
                  {myI18n._('ASSESMENT_NAME')}: {' '}
                  <Text style={surveyPDFStyles.text}>{survey.name}; </Text>
                </Text>
                {!survey.containsAggragatedData && (
                  <Text style={surveyPDFStyles.organizationDetailsBold}>
                    {myI18n._('Submission date')}: {' '}
                    <Text style={surveyPDFStyles.text}>
                      {moment
                        .utc(survey.modifiedDate)
                        .format(dateFormat)};
                    </Text>
                  </Text>
                )}

              </Text>
            </View>
          </View>

          <Text style={[surveyPDFStyles.paragraphTitle, { marginTop: 5 }]}>
            {myI18n._('ASSESSMENT BY KEY THEMES')}
          </Text>
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginLeft: -5,
              marginRight: -5
            }}
          >
            {Object.keys(data.sections)
              .filter((section) => scoringData[section])
              .map((item, index) => {
                const sectionData = data.sections[item]
                const percent = (sectionData.percent * 100).toFixed(0) + '%'
                let sectionText = ''
                const name = scoringData[item].name
                scoringData[item].scoreTresholds.forEach((threshold) => {
                  if (sectionData.percent > threshold.min) {
                    sectionText = survey?.containsAggragatedData
                      ? threshold.textAggregated
                      : threshold.text
                  }
                })

                return (
                  <View key={index} style={{ width: '50%', padding: 5 }}>
                    <KeyTheme
                      item={item}
                      name={name}
                      percent={percent}
                      sectionText={sectionText}
                      skippedSections={skippedSections}
                    />
                  </View>
                )
              })}
            <View style={{ width: '50%', padding: 5 }}>
              <KeyTheme
                item={{}}
                skippedSections={[]}
                averageName={myI18n._('AVERAGE SCORE')}
                averagePercent={`${scoreData.averageScore.toFixed(0)}%`}
                averageText={
                  survey.containsAggragatedData
                    ? (
                      <>
                        {myI18n._('SCORE_CARD_AGGREGATED_OVERALL_SCORE_OF')}{' '}
                        {scoreData.averageScore.toFixed(0)}%.{' '}
                        {scoreData.lowestScoreSection && (
                          <>
                            {myI18n._('PRIMARILY_FOCUS_ON')}{' '}
                            {getPdfTextFromSectionsArray({
                              data: scoreData.lowestScoreSection
                            })}
                          </>
                        )}{' '}
                        {myI18n._('SCORE_CARD_THEY_HAVE')}{' '}
                        {myI18n._(scoreData.understanding)}{' '}
                        {myI18n._('SCORE_CARD_AGGREGATED_UNDERSTANDING')}
                      </>
                      )
                    : (
                      <>
                        {myI18n._('You received an overall score of')}{' '}
                        {scoreData.averageScore.toFixed(0)}%
                        .{' '}
                        {scoreData.lowestScoreSection && (
                          <>
                            {myI18n._('You should primarily focus on')}{' '}
                            {getPdfTextFromSectionsArray({
                              data: scoreData.lowestScoreSection
                            })}
                          </>
                        )}{' '}
                        {myI18n._('You have a')}{' '}
                        {myI18n._(scoreData.understanding)}
                        {' '}
                        {myI18n._('understanding of your overall capacity.')}.
                      </>
                      )
                    }
              />
            </View>
          </View>
          {!esat && (
            <View>
              <View wrap={false} style={{ marginTop: 30 }}>
                <View style={{ flexDirection: 'row' }}>
                  <View style={{ width: '50%' }}>
                    <Text style={surveyPDFStyles.paragraphTitle}>
                      {myI18n._('SELF-GENERATED PRIORITIES')}
                    </Text>
                    {importanceChartImg &&
                      <View style={{ flexDirection: 'row' }}>
                        <View
                          style={{
                            display: 'flex',
                            justifyContent: 'center'
                          }}
                        >
                          {chartPayload?.map((entry, index) => (
                            <View
                              key={index}
                              style={{
                                flexDirection: 'row',
                                marginBottom: 3
                              }}
                            >
                              <View
                                style={{
                                  width: 7,
                                  height: 7,
                                  backgroundColor: entry.color,
                                  marginRight: 8
                                }}
                              />
                              <Text style={surveyPDFStyles.text}>
                                {entry.value}
                              </Text>
                            </View>
                          ))}
                        </View>
                        <View style={{ width: '100%', alignItems: 'center' }}>
                          <Image src={importanceChartImg} style={surveyPDFStyles.chartImage} />
                        </View>
                      </View>}
                    <Text style={surveyPDFStyles.text}>
                      {highestImportanceSections && highestImportanceSections.length === 4
                        ? survey.containsAggragatedData
                          ? myI18n._('SCORE_CARD_AGGREGATED_EQUAL_IMPORTANCE')
                          : myI18n._('Your organization places equal importance on all key themes.')
                        : (
                          <>
                            {survey.containsAggragatedData
                              ? myI18n._('SCORE_CARD_AGGREGATED_HIGHEST_IMPORTANCE')
                              : myI18n._('Your organization places the highest importance on')}
                            {' '}
                            {getPdfTextFromSectionsArray({ data: highestImportanceSections })}
                          </>
                          )}
                    </Text>
                  </View>
                  <View style={{ width: '50%' }}>
                    <Text style={[surveyPDFStyles.paragraphTitle, { paddingBottom: 40 }]}>
                      {myI18n._('ORGANIZATIONAL FUTURE')}
                    </Text>
                    <View>
                      {Object.keys(data.Future).map((item, index) => (
                        <FutureAspect
                          containsAggragatedData={survey.containsAggragatedData}
                          item={item}
                          data={data}
                          pdfStyles={surveyPDFStyles}
                          key={index}
                          renderPdf
                        />
                      ))}
                    </View>
                  </View>
                </View>
              </View>
            </View>
          )}
        </View>
        {!esat &&
          <View style={[surveyPDFStyles.scoreSummary, { marginTop: 20 }]}>
            <Text style={[surveyPDFStyles.paragraphTitle, { color: '#ffff' }]}>
              {myI18n._('SCORE_CARD_SUMMARY')}
            </Text>
            <Text style={[surveyPDFStyles.text, { color: '#ffff' }]}>
              {survey.containsAggragatedData
                ? (
                  <>
                    {myI18n._('SCORE_CARD_AGGREGATED_BELIEVE_OVERALL_AVERAGE')}{' '}
                    {scoreData.averageScore.toFixed(0)}%.
                  </>
                  )
                : (
                  <>
                    {myI18n._('Your organization achieved an overall average of')}{' '}
                    {scoreData.averageScore.toFixed(0)}%.
                  </>
                  )}
            </Text>
            <Text style={[surveyPDFStyles.text, { color: '#ffff' }]}>
              {myI18n._(scoreData.adjustmentPainText)}{' '}
              {myI18n._(scoreData.adjustmentValue)}%.
            </Text>
            <Text style={[surveyPDFStyles.text, { color: '#ffff' }]}>
              {myI18n._(futureText)}
            </Text>
          </View>}
        {!esat && !survey.containsAggragatedData && (
          <View style={surveyPDFStyles.page} break>
            <AvaliableActions
              user={user}
              template={{ id: survey.templateId }}
              sectionScores={sectionScores}
              scoreCard={scoreCard}
              actionTemplates={actionTemplates}
              skippedSections={skippedSections}
              surveyAnswers={JSON.parse(survey.answer)}
            />
          </View>
        )}
      </Page>
    </Document>
  )
}

export default SurveyScorePdf
